import { useState } from 'react';
import { useAwaitControl } from 'react-redux-await-control';
import { useTheme } from 'styled-components';
import { message } from 'antd';
import toMoney from '~/common/masked/toMoney';
import { Button, Divider, Flex, Typography } from '~/ui/components';
import { Quote } from '~/ui/assets/icons';
import { PaymentOptionResponse } from '~/typings/entities/Cobranca';
import { backoffice } from '~/services';
import { getLoggedUser } from '~/store/user/actions';
import { getContractDetails } from '~/store/contracts/actions';
import useAsyncActionStatus from '~/hooks/useAsyncActionStatus';
import { CODIGO_CREDOR } from './constants';
import { useQuitacaoUnavailableGuard } from './QuitacaoUnavailableModal';

type Installment = {
  installmentNumber: number;
  amount: number;
  ccb: string;
  [key: string]: any;
};

export type StepReviewProps = {
  selectedInstallments: Installment[];
  totalAmount: number;
  dueDate?: string;
  personName?: string;
  document?: string;
  contractId?: string;
  ticketId?: string;
  conversationId?: string;
  codigoCliente?: string;
  codigoSolicitacao?: string;
  ofertaOpcoes?: PaymentOptionResponse[];
  isSuccess?: boolean;
  onSuccess: () => void;
};

export function StepReview({
  selectedInstallments,
  totalAmount,
  dueDate,
  personName,
  document,
  codigoCliente,
  ticketId,
  conversationId,
  codigoSolicitacao,
  ofertaOpcoes,
  isSuccess,
  onSuccess,
}: StepReviewProps) {
  const theme = useTheme();
  const primaryColor = theme?.colors?.primary?.main || '#C4125D';

  const checkQuitacaoUnavailable = useQuitacaoUnavailableGuard();

  const loggedUserControl = useAwaitControl(getLoggedUser);
  const userId = loggedUserControl.result()?.id;

  const [isLoading, setIsLoading] = useState(false);
  const [pixCode, setPixCode] = useState<string | null>(null);
  const [pixPDF, setPixPDF] = useState<string | null>(null);

  const { result: details } = useAsyncActionStatus(getContractDetails);

  const [refParcelamento, setRefParcelamento] = useState<string | null>(null);

  const installmentNumbers = selectedInstallments.map((i) => i.installmentNumber).join(', ');
  const ccb = selectedInstallments[0]?.ccb || '-';

  const formattedDueDate = dueDate ? dueDate.split('-').reverse().join('/') : '-';

  const formattedDocument = details?.person?.document
    ? details?.person?.document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    : '-';

  const handleEmitir = async () => {
    const chaveCliente = { codigoCredor: CODIGO_CREDOR, codigoCliente };
    const codigoPlano = ofertaOpcoes?.[0]?.plans?.[0]?.planCode;

    setIsLoading(true);
    try {
      const { data: confirmarData } = await backoffice.post('/billing/offer/confirm', {
        chaveCliente,
        codigoSolicitacaoOpcoesPagamento: codigoSolicitacao,
        codigoPlano,
        dadosAdicionais: { operador: String(userId ?? '') },
      });

      if (checkQuitacaoUnavailable(confirmarData)) return;

      const codigoParcelamento = confirmarData?.installment?.installmentCode;

      const dataVencimento = ofertaOpcoes?.[0]?.firstPaymentDate ?? dueDate;
      const { data: pixData } = await backoffice.post('/billing/pix', {
        chaveCliente,
        codigoParcelamento,
        dataVencimentoParcela: dataVencimento,
        dataVencimentoPix: dataVencimento,
        origin: 'Backoffice',
        origem: 'Backoffice',
        backofficeId: ticketId || conversationId,
        pdf: true,
      });

      if (checkQuitacaoUnavailable(pixData)) return;

      setPixCode(pixData?.pix?.pixBarCode ?? null);
      setPixPDF(pixData?.pixPDF ?? null);

      setRefParcelamento(pixData?.pix?.documentNumber ?? null);

      onSuccess();
    } catch {
      message.error('Erro ao gerar pagamento');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyPix = () => {
    if (pixCode) navigator.clipboard.writeText(pixCode);
  };

  const handleDownloadPdf = () => {
    if (!pixPDF) return;
    const byteArray = Uint8Array.from(atob(pixPDF), (c) => c.charCodeAt(0));
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = `boleto.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Flex direction="column" style={{ height: 'calc(100vh - 120px)', marginTop: 16 }} gap={16}>
      <Quote height={40} width={40} color={primaryColor} />
      <Flex direction="column" style={{ flex: 1, overflow: 'auto', paddingTop: 16 }} gap={8}>
        <Flex direction="column" gap={8}>
          <Flex justify="space-between" align="center">
            <Typography type="bodySmall" color="element.secondary">
              Vencimento
            </Typography>
            <Typography type="bodySmall" weight={600}>
              {formattedDueDate}
            </Typography>
          </Flex>
          <Flex justify="space-between" align="center">
            <Typography type="bodySmall" color="element.secondary">
              Valor
            </Typography>
            <Typography type="bodyLarge" weight={600}>
              {toMoney(totalAmount)}
            </Typography>
          </Flex>
        </Flex>

        <Flex
          direction="column"
          style={{ marginTop: 56, marginBottom: 56, borderBottom: `1px solid ${theme?.colors?.border?.main}` }}
          gap={8}
        >
          <Flex justify="space-between" align="center">
            <Typography type="bodySmall" color="element.secondary">
              Parcela
            </Typography>
            <Typography type="bodySmall" weight={600}>
              {installmentNumbers || '-'}
            </Typography>
          </Flex>
          <Flex justify="space-between" align="center">
            <Typography type="bodySmall" color="element.secondary">
              Ref-Parcelamento
            </Typography>
            <Typography type="bodySmall" weight={600}>
              {refParcelamento || '-'}
            </Typography>
          </Flex>
          <Flex justify="space-between" align="center">
            <Typography type="bodySmall" color="element.secondary">
              Contrato
            </Typography>
            <Typography type="bodySmall" weight={600}>
              {ccb}
            </Typography>
          </Flex>
        </Flex>

        <Flex
          direction="column"
          style={{ marginTop: 56, borderBottom: `1px solid ${theme?.colors?.border?.main}` }}
          gap={8}
        >
          <Flex justify="space-between" align="center">
            <Typography type="bodySmall" color="element.secondary">
              Cliente
            </Typography>
            <Typography type="bodySmall" weight={600}>
              {details?.person?.name || '-'}
            </Typography>
          </Flex>
          <Flex justify="space-between" align="center">
            <Typography type="bodySmall" color="element.secondary">
              CPF
            </Typography>
            <Typography type="bodySmall" weight={600}>
              {formattedDocument || '-'}
            </Typography>
          </Flex>
        </Flex>

        <Flex justify="space-between" align="center">
          <Typography type="bodySmall" color="element.secondary">
            Método pagamento
          </Typography>
          <Typography type="bodySmall" weight={600}>
            {ofertaOpcoes?.[0]?.optionType || 'PIX'}
          </Typography>
        </Flex>
      </Flex>

      <Flex direction="column" bg="background.primary">
        <Divider lineType="solid" />
        <Flex justify="flex-end" pv={16} gap={8}>
          {!isSuccess && (
            <Button variant="contained" loading={isLoading} onClick={handleEmitir}>
              Gerar pagamento
            </Button>
          )}
          {isSuccess && pixCode && (
            <Button variant="outline" icon="Copy" onClick={handleCopyPix}>
              Copiar código Pix
            </Button>
          )}
          {isSuccess && pixPDF && (
            <Button variant="contained" icon="Download" onClick={handleDownloadPdf}>
              Baixar boleto
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
